/**
 * Performance Monitor
 * Tracks frame rate, memory usage, and rendering performance
 */

import { audioState, managers } from './state.js';

export class PerformanceMonitor {
  constructor() {
    this.enabled = false;
    this.frameCount = 0;
    this.droppedFrames = 0;
    this.lastFrameTime = 0;
    this.frameTimes = [];
    this.heapHistory = [];
    this.updateInterval = null;
    this.heapSampleInterval = null;

    // DOM elements (cached)
    this.elements = {
      fps: document.getElementById('perfFps'),
      frameTime: document.getElementById('perfFrameTime'),
      dropped: document.getElementById('perfDropped'),
      heap: document.getElementById('perfHeap'),
      heapBar: document.getElementById('perfHeapBar'),
      heapGrowth: document.getElementById('perfHeapGrowth'),
      bufferSize: document.getElementById('perfBufferSize'),
      sampleRate: document.getElementById('perfSampleRate'),
      visType: document.getElementById('perfVisType'),
      context: document.getElementById('perfContext'),
      warning: document.getElementById('perfWarning'),
      stats: document.getElementById('perfMonitorStats')
    };
  }

  start() {
    if (this.enabled) return;
    if (!this.elements.stats) return;
    this.enabled = true;
    this.frameCount = 0;
    this.droppedFrames = 0;
    this.frameTimes = [];
    this.heapHistory = [];
    this.lastFrameTime = performance.now();

    this.elements.stats.style.display = 'block';

    // Update UI every 500ms
    this.updateInterval = setInterval(() => this.updateUI(), 500);

    // Sample heap every 5 seconds for growth rate
    this.heapSampleInterval = setInterval(() => this.sampleHeap(), 5000);
  }

  stop() {
    if (!this.enabled) return;
    this.enabled = false;

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.heapSampleInterval) {
      clearInterval(this.heapSampleInterval);
      this.heapSampleInterval = null;
    }

    if (this.elements.stats) this.elements.stats.style.display = 'none';
  }

  recordFrame() {
    if (!this.enabled) return;

    const now = performance.now();
    const frameTime = now - this.lastFrameTime;
    this.lastFrameTime = now;

    // Track frame times (rolling window of 60 frames)
    this.frameTimes.push(frameTime);
    if (this.frameTimes.length > 60) {
      this.frameTimes.shift();
    }

    // Count dropped frames (> 33ms = less than 30fps)
    if (frameTime > 33) {
      this.droppedFrames++;
    }

    this.frameCount++;
  }

  sampleHeap() {
    if (!this.enabled || !performance.memory) return;

    const heapMB = performance.memory.usedJSHeapSize / (1024 * 1024);
    const timestamp = Date.now();

    this.heapHistory.push({ heapMB, timestamp });

    // Keep last 12 samples (1 minute of data)
    if (this.heapHistory.length > 12) {
      this.heapHistory.shift();
    }
  }

  updateUI() {
    if (!this.enabled || !this.elements.stats) return;

    // FPS calculation
    if (this.frameTimes.length > 0) {
      const avgFrameTime = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
      const fps = 1000 / avgFrameTime;
      this.elements.fps.textContent = fps.toFixed(1);
      this.elements.frameTime.textContent = avgFrameTime.toFixed(1) + ' ms';

      // Color code FPS
      this.elements.fps.className = 'perf-value' + (fps < 30 ? ' perf-warning-color' : fps < 50 ? ' perf-caution-color' : '');
    }

    // Dropped frames
    this.elements.dropped.textContent = this.droppedFrames;

    // Memory (Chrome only)
    if (performance.memory) {
      const usedMB = performance.memory.usedJSHeapSize / (1024 * 1024);
      const totalMB = performance.memory.jsHeapSizeLimit / (1024 * 1024);
      const percent = (usedMB / totalMB) * 100;

      this.elements.heap.textContent = usedMB.toFixed(1) + ' / ' + totalMB.toFixed(0) + ' MB';
      this.elements.heapBar.style.width = percent + '%';

      // Color code heap bar
      this.elements.heapBar.className = 'perf-progress-bar' +
        (percent > 50 ? ' perf-warning-color' : percent > 25 ? ' perf-caution-color' : '');

      // Heap growth rate
      if (this.heapHistory.length >= 2) {
        const oldest = this.heapHistory[0];
        const newest = this.heapHistory[this.heapHistory.length - 1];
        const elapsedMin = (newest.timestamp - oldest.timestamp) / 60000;
        const growthPerMin = (newest.heapMB - oldest.heapMB) / elapsedMin;

        this.elements.heapGrowth.textContent = (growthPerMin >= 0 ? '+' : '') + growthPerMin.toFixed(2) + ' MB/min';
        this.elements.heapGrowth.className = 'perf-value' +
          (growthPerMin > 1 ? ' perf-warning-color' : growthPerMin > 0.5 ? ' perf-caution-color' : '');

        // Show warning if growing too fast
        this.elements.warning.style.display = growthPerMin > 1 ? 'block' : 'none';
      }
    } else {
      this.elements.heap.textContent = 'N/A (Chrome only)';
      this.elements.heapGrowth.textContent = 'N/A';
    }

    // Audio context info
    if (audioState.audioContext) {
      this.elements.bufferSize.textContent = audioState.analyserNode ? audioState.analyserNode.fftSize : '--';
      this.elements.sampleRate.textContent = audioState.audioContext.sampleRate + ' Hz';
    }

    // Visualizer info
    if (managers.visualizerManager) {
      const current = managers.visualizerManager.getVisualizer();
      if (current) {
        this.elements.visType.textContent = current.constructor.displayName || current.constructor.name;
        this.elements.context.textContent = current.constructor.contextType || '2d';
      }
    }
  }
}

// Singleton instance
export const perfMonitor = new PerformanceMonitor();
